<script lang="ts">
import type { ToastProps } from 'types';
</script>

<script setup lang="ts">
import {
  ToastProvider,
  ToastViewport,
  ToastRoot,
  ToastTitle,
  ToastDescription,
  ToastAction,
  ToastClose
} from 'reka-ui';

import { useEventListener } from '@vueuse/core';
import { Ref, ref } from 'vue';

const toasts: Ref<ToastProps[]> = ref<ToastProps[]>([]);

const removeToast = (id: string) => {
  const idx = toasts.value.findIndex(t => t.id === id);
  if (idx === -1) return;
  toasts.value.splice(idx, 1);
};

// Only attach DOM listeners on client
if (globalThis.window !== undefined) {
  useEventListener(globalThis.window, 'e-toast', (e: Event) => {
    const detail = (e as CustomEvent<Partial<ToastProps> & { id?: string }>).detail ?? {};
    if (!detail.id || !detail.title) return;

    const idx = toasts.value.findIndex(t => t.id === detail.id);
    if (idx === -1) {
      const duration = detail.duration ?? 4000;
      toasts.value.push({
        id: detail.id,
        title: detail.title,
        ...detail,
        open: true,
        duration
      } as ToastProps);
    } else {
      const prev = toasts.value[idx];
      const duration = detail.duration ?? prev.duration ?? 4000;
      toasts.value[idx] = { ...prev, ...detail, open: true, duration };
    }
  });

  useEventListener(globalThis.window, 'e-toast-update', (e: Event) => {
    const payload = (e as CustomEvent<{ id: string; props: Partial<ToastProps> }>).detail;
    if (!payload?.id) return;

    const idx = toasts.value.findIndex(t => t.id === payload.id);
    if (idx === -1) return;

    const updated = { ...toasts.value[idx], ...payload.props };
    toasts.value[idx] = updated;

    if (payload.props?.open === false) {
      removeToast(payload.id);
    }
  });

  useEventListener(globalThis.window, 'e-toast-remove', (e: Event) => {
    const { id } = (e as CustomEvent<{ id: string }>).detail ?? {};
    if (id) removeToast(id);
  });
}

// Expose for tests; keep type loose to avoid TS2742 in dts
defineExpose<{ toasts: unknown }>({ toasts });
</script>

<template>
  <toast-provider class="e-toast-root">
    <toast-viewport class="e-toast-viewport" />
    <toast-root
      v-for="t in toasts"
      :key="t.id"
      :open="t.open"
      :duration="t.duration"
      class="e-toast"
    >
      <toast-title v-if="t.title">{{ t.title }}</toast-title>
      <toast-description v-if="t.description">{{ t.description }}</toast-description>

      <toast-action
        v-for="(action, i) in t.actions"
        :key="i"
        as-child
        alt-text=""
      >
        <button
          type="button"
          @click="action.onClick"
        >
          {{ action.label ?? `Action ${i}` }}
        </button>
      </toast-action>

      <toast-close v-if="t.close !== false" />
    </toast-root>
  </toast-provider>
</template>
